import type { Scene } from '@babylonjs/core/scene';

import type { ArmourClass } from '../../net/protocol';
import { type Cell, FACE, GEAR } from './atlas';
import type { ModelPalette, MaterialName } from './palette';
import { box, capsule, cylinder, lathe, sphere, torus, type Rig } from './rig';
import type { WornGear } from './types';

/**
 * The three armour silhouettes, per `doc/grind/design/ARMOR-VISUALS.md`.
 *
 * **Proportion and mass, not detail.** A character is roughly 30 × 45 device pixels at
 * the gameplay camera and the pixel post-process eats anything finer, so studs and
 * buckles would cost draw calls and be invisible — the same reasoning that gives six
 * monster rigs to thirteen archetypes.
 *
 * **But width is a weaker lever than it looks, and measuring caught that.** The
 * crusader carries a longsword and a kite shield, and those set the overall outline:
 * only plate's pauldrons reach past the shield, so plate is the one class that widens
 * the silhouette (1.22 units against 1.02). Cloth and leather come out within a
 * whisker of the bare body. What actually tells those two apart at distance is
 * **below the waist and in colour** — the robe's hem reaching to the shins, its hood
 * mantle, dark cloth against rust against steel, and the red tabard block being
 * present or replaced by a single strap. `silhouette.test.ts` measures the built rig
 * and asserts each of those, rather than trusting the profile numbers to imply them.
 *
 * **Cosmetic only, and that is load-bearing.** There is no mitigation formula
 * anywhere in `doc/grind/design/`, and armour is summed from gear and read by
 * nothing (`doc/grind/design/CLASSES-ATTRIBUTES.md`). A player must not be able to infer a defensive
 * number from a silhouette, so the classes differ in shape and never in a way that
 * suggests one is "better" — plate is bulkier, not stronger.
 *
 * The profile is a plain value so it can be tested without a scene, which is the
 * split `poses.ts` has. Only `buildArmour` touches Babylon.
 */
export interface ArmourProfile {
  /** Chest block: width, height, depth. Plate is the widest, cloth the narrowest. */
  chest: readonly [number, number, number];
  /** Height of the chest block's centre above the torso joint. */
  chestY: number;
  /** Shoulder mass, or null for a silhouette with no shoulder line at all. */
  pauldron: readonly [number, number, number] | null;
  /** Hem hanging from the waist — a robe's skirt, a knight's faulds. */
  skirt: readonly [number, number, number] | null;
  /** Height of the hem's centre; negative, because it hangs below the waist. */
  skirtY: number;
  /** The front panel: a tabard, a single strap, or nothing. */
  overlay: 'tabard' | 'strap' | 'none';
  /** Which shared material the body uses. */
  material: MaterialName;
  /** A wider hood mantle, for the robed silhouette. */
  mantle: boolean;
  /**
   * The painted cells this body is surfaced with.
   *
   * **The gear sheet's rows are the armour classes**, which is why this art needed no new
   * wire field: row 1 is fabric, row 2 is leather, row 3 is metal, and the snapshot has
   * carried `armour` since the three silhouettes shipped. So a cell here is a second
   * reading of a field the client already had, not a new choice for anybody to make.
   *
   * Every one of these is a `SWATCH` except the robe's hem, and that is not a coincidence:
   * a chest, a skirt and a pauldron are all boxes seen from several sides at once, and a
   * cell painted as a garment would put a second set of armholes on whichever face was
   * turned to the camera. The hem is the exception because the tattered-robe cell *is* a
   * hem — points and gold embroidery along its bottom edge — and a skirt's front is the
   * face a player reads.
   *
   * `material` stays beside these and keeps doing two jobs: it is the fallback when
   * `?textures=0` or safe mode wants the plainest shader Babylon will compile, and it is
   * where the emissive lift and specular come from, so a textured body reads at the same
   * value against the floor as the flat colour it replaced.
   */
  cells: {
    /** Chest, and the skirt unless `hem` overrides it. */
    body: Cell;
    /** The shoulder mass. Null exactly when `pauldron` is. */
    shoulder: Cell | null;
    /** The hem, when it is painted as one rather than as more of the body. */
    hem: Cell | null;
    /** The hood mantle. Null exactly when `mantle` is false. */
    mantle: Cell | null;
  };
}

/**
 * The un-armoured body: mail under a tabard, with medium pauldrons.
 *
 * This is exactly what the crusader looked like before armour classes existed, so a
 * character with a bare chest is visually unchanged — there is no "naked" look to
 * design, and a fresh player should not appear to have lost something.
 */
const BARE: ArmourProfile = {
  chest: [0.62, 0.56, 0.36],
  chestY: 0.34,
  pauldron: [0.24, 0.2, 0.34],
  skirt: null,
  skirtY: 0,
  overlay: 'tabard',
  material: 'steel',
  mantle: false,
  // Mail, because this body has always been described as a mailed figure under a tabard
  // and the sheet has a cell of interlocking rings. The one place the art and the prose
  // that predates it agree exactly.
  cells: { body: GEAR.chainmail, shoulder: GEAR.ironPlate, hem: null, mantle: null },
};

const PROFILES: Record<ArmourClass, ArmourProfile> = {
  // Robes. Narrow through the chest with no shoulder line and a long hem, so the
  // silhouette is a column rather than a wedge.
  CLOTH: {
    chest: [0.5, 0.54, 0.3],
    chestY: 0.34,
    pauldron: null,
    skirt: [0.56, 0.72, 0.4],
    skirtY: -0.3,
    overlay: 'none',
    material: 'cloth',
    mantle: true,
    // Burlap rather than the sheet's crimson wool, and it is the one cell choice here that
    // was made against the prettier option. Crimson is the tabard's colour: `plate` and the
    // bare body both wear that block of red, and it is described as the thing that reads
    // first at distance, so dressing the robe in it would put the same red on all three
    // classes and spend the one strong colour cue the set has. Sackcloth keeps cloth dark
    // and drab, which is what `material: 'cloth'` already said it was.
    //
    // The hem is the tattered-robe cell, whose gold-embroidered points *are* a hem — and
    // the fur mantle over it is the shape that says caster without a shoulder line.
    cells: { body: GEAR.burlap, shoulder: null, hem: GEAR.robeTattered, mantle: GEAR.fur },
  },
  // A padded jacket. Between the two: some shoulder, a short hem, and a single
  // strap across the chest instead of a tabard.
  LEATHER: {
    chest: [0.58, 0.55, 0.34],
    chestY: 0.34,
    pauldron: [0.18, 0.14, 0.3],
    skirt: [0.48, 0.26, 0.34],
    skirtY: -0.12,
    overlay: 'strap',
    material: 'rust',
    mantle: false,
    // Plain worn leather on the body with the studded brigandine on the shoulders: the studs
    // are the one cell in the row with a regular pattern, so putting them where the shoulder
    // mass is gives leather a shoulder line made of detail rather than of size — which is the
    // half of this silhouette that width could never carry, since the shield sets the outline.
    cells: { body: GEAR.leatherWorn, shoulder: GEAR.brigandine, hem: null, mantle: null },
  },
  // A cuirass. The widest chest and the heaviest shoulders on any body, which is
  // also what gives it the largest measured collider — see buildArmour.
  PLATE: {
    chest: [0.72, 0.6, 0.42],
    chestY: 0.35,
    pauldron: [0.3, 0.26, 0.4],
    skirt: [0.6, 0.24, 0.4],
    skirtY: -0.12,
    overlay: 'tabard',
    material: 'steel',
    mantle: false,
    // Hammered iron plate, not the sheet's three cuirass cells. Those are painted as
    // cuirasses — armholes, a waist, a neck — and they are the best-looking metal on the
    // sheet, which is exactly the trap: a chest is a box the camera sees the side and top of
    // at this angle, so a garment-shaped cell puts a second neckline down the ribs. The
    // riveted panel tiles, and a knight is plate everywhere rather than one breastplate.
    cells: { body: GEAR.ironPlate, shoulder: GEAR.ironPlate, hem: GEAR.ironPlate, mantle: null },
  },
};

/**
 * The profile for a wire value.
 *
 * Anything unrecognised falls back to the bare body rather than throwing or
 * rendering nothing: the server sends no `armour` field for a bare chest, and a
 * newer server naming a fourth class must degrade to a body rather than to an
 * invisible player.
 */
export function armourProfile(armour?: string | null): ArmourProfile {
  switch (armour) {
    case 'CLOTH':
      return PROFILES.CLOTH;
    case 'LEATHER':
      return PROFILES.LEATHER;
    case 'PLATE':
      return PROFILES.PLATE;
    default:
      return BARE;
  }
}

/** Every class a profile exists for, for tests and for iteration. */
export const ARMOUR_CLASSES: readonly ArmourClass[] = ['CLOTH', 'LEATHER', 'PLATE'];

/**
 * Builds the body layer onto a rig's torso.
 *
 * Called once at construction. **There is no runtime swap**, and that is a deliberate
 * departure from the spec's pseudo-code, which toggles `isVisible` across pre-built
 * layers: `entities.ts` rebuilds the whole model when the snapshot's armour changes,
 * which happens only on an equip.
 *
 * Rebuilding is the simpler of the two and it keeps `measureRig` honest. Pre-building
 * every layer would put all three bodies in `rig.parts`, and since the collider is
 * measured from that list, **every character's hitbox would be plate-sized whatever
 * they wore** — hidden meshes still have bounds. Rebuilding means the box always
 * matches the body on screen.
 *
 * That is correctness rather than a feature today: a *player's* collider is not
 * pickable at all (`finaliseParts`' `clickable` is false for players, because a box
 * on your own character sits between the cursor and the floor). So nothing currently
 * clicks these boxes. It matters the moment the same layering reaches monsters, or
 * when PvP makes another player a target — and it would have been a silent wrong
 * answer waiting there either way.
 */
export function buildArmour(
  scene: Scene,
  rig: Rig,
  palette: ModelPalette,
  profile: ArmourProfile,
  accent: MaterialName,
): void {
  const parts = rig.parts;
  const body = palette.cell(profile.cells.body, profile.material);
  const dark = palette.get('darkSteel');
  // The belt is the one part of the body dressed from row 4 rather than from its class's own
  // row: every class has a belt, and the sheet paints one. `bronze` stays as its fallback and
  // its lift, so the warm metal that makes the player findable in a fight survives
  // `?textures=0`.
  const beltMat = palette.cell(GEAR.belt, 'bronze');

  // **The chest is an ellipsoid, and that is most of the Phase 41 body.** A box chest has a
  // flat front the light hits evenly and two flat sides in shadow, so at the gameplay camera
  // it read as a crate with a head on it — the owner's word was *square*. An ellipsoid the
  // same width and depth is shaded continuously round its whole circumference, and its
  // outline narrows toward the shoulders and the waist without a single extra part. It is
  // 1.15× the profile's height so the top of it fills the space under the pauldrons the way
  // a ribcage does, rather than stopping at a flat lid.
  sphere(scene, rig.torso, parts, {
    name: 'cru-chest',
    size: [profile.chest[0], profile.chest[1] * 1.15, profile.chest[2]],
    at: [0, profile.chestY, 0],
    material: body,
    segments: 8,
  });
  // The waist tapers into the belt: a frustum, wider at the ribs than the hips.
  cylinder(scene, rig.torso, parts, {
    name: 'cru-waist',
    height: 0.26,
    diameterTop: Math.min(0.46, profile.chest[0] * 0.8),
    diameterBottom: 0.38,
    at: [0, 0.04, 0],
    material: dark,
    tessellation: 8,
  });

  if (profile.overlay === 'tabard') {
    // The block of colour that reads first at distance — a hanging panel, so it stays flat.
    box(scene, rig.torso, parts, {
      name: 'cru-tabard',
      size: [0.3, 0.64, 0.05],
      at: [0, 0.16, profile.chest[2] / 2 + 0.01],
      material: palette.get('tabard'),
    });
  } else if (profile.overlay === 'strap') {
    // A baldric across the chest rather than a bar across it: rolled into the ellipsoid.
    torus(scene, rig.torso, parts, {
      name: 'cru-strap',
      diameter: profile.chest[0] * 0.98,
      thickness: 0.05,
      at: [0, profile.chestY + 0.02, 0],
      rotation: [0.25, 0, 0.9],
      material: dark,
      tessellation: 12,
    });
  }

  // A belt is a ring, not a slab.
  torus(scene, rig.torso, parts, {
    name: 'cru-belt',
    diameter: 0.4,
    thickness: 0.085,
    at: [0, 0.0, 0],
    material: beltMat,
    tessellation: 10,
  });

  if (profile.skirt) {
    // A hem flares: a robe is a cone from the belt, faulds are a shorter one.
    cylinder(scene, rig.torso, parts, {
      name: 'cru-skirt',
      height: profile.skirt[1],
      diameterTop: profile.skirt[0] * 0.78,
      diameterBottom: profile.skirt[0] * 1.05,
      at: [0, profile.skirtY, 0],
      material: palette.cell(profile.cells.hem ?? profile.cells.body, profile.material),
      tessellation: 8,
    });
  }

  if (profile.pauldron) {
    const size = profile.pauldron;
    // Seated a little further onto the shoulder than the box was: a dome's outer edge is its
    // equator, where a box's was its whole face.
    const x = profile.chest[0] / 2 + size[0] / 2 - 0.07;
    // The accent is the local player's blue rim, and it stays **flat** on purpose: it is how
    // you find yourself in a crowd, so it must not be a painting that happens to look like
    // everybody else's shoulders. A texture goes on only where the accent is not doing that
    // job — which is every other player and every NPC.
    const shoulder =
      accent === 'selfGlow' || !profile.cells.shoulder
        ? palette.get(accent)
        : palette.cell(profile.cells.shoulder, accent);
    for (const [side, sign] of [
      ['R', -1],
      ['L', 1],
    ] as const) {
      // A dome over the shoulder, tipped outward: the shape of every pauldron ever forged,
      // and the one that reads as armour from above where a cube read as luggage.
      // Flat rather than tall — 1.1× the profile's height, not a ball — because the local
      // player's accent is painted flat and unlit, and a round lit-blue ball on each
      // shoulder read as two lanterns in the first browser capture.
      sphere(scene, rig.torso, parts, {
        name: `cru-pauldron${side}`,
        size: [size[0] * 1.2, size[1] * 0.8, size[2]],
        at: [sign * x, 0.47, 0],
        rotation: [0, 0, sign * -0.3],
        material: shoulder,
        segments: 6,
        slice: 0.55,
      });
    }
  }

  if (profile.mantle) {
    // A robe's hood sits over the shoulders, which is the shape that says "caster"
    // without a shoulder line to say anything else.
    cylinder(scene, rig.torso, parts, {
      name: 'cru-mantle',
      height: 0.18,
      diameterTop: 0.34,
      diameterBottom: 0.62,
      at: [0, 0.56, 0],
      material: palette.cell(profile.cells.mantle ?? profile.cells.body, profile.material),
      tessellation: 8,
    });
  }
}

// ── The other four armour slots ─────────────────────────────────────────────
//
// Phase 41. `EntitySnapshot.head/hands/legs/feet` carry the worn class of each of the
// remaining armour slots, and each dresses its own part of the rig. **The chest still
// decides the body** — `ARMOR-VISUALS.md`'s rule and `TestOnlyTheChestDecidesTheBody` both
// still hold — because a helm changes the head and a boot changes the foot, and neither
// changes the torso the collider is measured around.
//
// Profiles are plain values here, builders below, the split every file in this directory
// has. Absent means the part the crusader always had, so a character with nothing on the
// wire is unchanged.

/** What sits on the head. Derived from the head slot, or the chest when the head is bare. */
export type HeadStyle = 'hood' | 'cap' | 'helm';

/**
 * Which headgear a character wears.
 *
 * The **head slot first**, which is new; the **chest second**, which is what the helm was
 * derived from when only the chest was on the wire — so a bare head over a plate cuirass
 * still gets the helm it has had since 2026-08-12 rather than suddenly losing it.
 */
export function headStyleFor(head?: string | null, chest?: string | null): HeadStyle {
  switch (head || chest) {
    case 'PLATE':
      return 'helm';
    case 'LEATHER':
      return 'cap';
    default:
      return 'hood';
  }
}

export interface HandsProfile {
  /** Forearm radius as a multiple of the bare arm's: a bracer thickens it, a gauntlet more. */
  cuff: number;
  /** A flared ring at the wrist — plate gauntlets have one, nothing else does. */
  flare: boolean;
  cell: Cell;
  material: MaterialName;
}

export interface LegsProfile {
  /** Thigh radius multiplier: cuisses are bulkier than hose. */
  girth: number;
  /** A dome over the knee. Plate only. */
  cop: boolean;
  cell: Cell;
  material: MaterialName;
}

export interface FeetProfile {
  /** How far the boot's shaft rises up the shin, world units. A shoe is nearly zero. */
  shaft: number;
  /** Toe box length. Sabatons are long and square; a shoe is short. */
  toe: number;
  cell: Cell;
  material: MaterialName;
}

/** The bare arm the crusader always had: a mail sleeve to the wrist. */
const BARE_HANDS: HandsProfile = { cuff: 1, flare: false, cell: GEAR.chainmail, material: 'steel' };
const HANDS: Record<ArmourClass, HandsProfile> = {
  CLOTH: { cuff: 1.0, flare: false, cell: GEAR.burlap, material: 'cloth' },
  LEATHER: { cuff: 1.18, flare: false, cell: GEAR.leatherWorn, material: 'rust' },
  PLATE: { cuff: 1.35, flare: true, cell: GEAR.ironPlate, material: 'steel' },
};

/** The bare leg: studded brigandine greaves, as the body was always described. */
const BARE_LEGS: LegsProfile = { girth: 1, cop: false, cell: GEAR.brigandine, material: 'darkSteel' };
const LEGS: Record<ArmourClass, LegsProfile> = {
  CLOTH: { girth: 0.95, cop: false, cell: GEAR.burlap, material: 'cloth' },
  LEATHER: { girth: 1.0, cop: false, cell: GEAR.leatherWorn, material: 'rust' },
  PLATE: { girth: 1.2, cop: true, cell: GEAR.ironPlate, material: 'steel' },
};

/** The bare foot: the worn-leather boot every class wore before boots were drawn. */
const BARE_FEET: FeetProfile = { shaft: 0.1, toe: 0.3, cell: GEAR.leatherWorn, material: 'rust' };
const FEET: Record<ArmourClass, FeetProfile> = {
  CLOTH: { shaft: 0.02, toe: 0.24, cell: GEAR.leatherWorn, material: 'cloth' },
  LEATHER: { shaft: 0.2, toe: 0.3, cell: GEAR.leatherWorn, material: 'rust' },
  PLATE: { shaft: 0.14, toe: 0.34, cell: GEAR.ironPlate, material: 'steel' },
};

/**
 * Where the face cell lands on the skull sphere.
 *
 * Babylon's sphere UVs put the front (+Z) at u = 0.75 and the top at v = 0 (measured under
 * `NullEngine`, `rig.test.ts` holds it). The picture is scaled to cover ~100° of the
 * circumference (360/100 = 3.6) centred on the front — 0.5 = 0.75·3.6 + offset — and, on the
 * vertical, the band from a third of the way down (under the brow) to just above the chin.
 */
export const FACE_FIT = { uScale: 3.6, vScale: 1.5, uOffset: 0.5 - 0.75 * 3.6, vOffset: -0.3 * 1.5 };

/** The hood covers this much of the circumference; the rest is the opening for the face. */
const HOOD_ARC = 0.7;
/** Turns the arc's gap to the front. See the hood in `buildHead`. */
const HOOD_YAW = -(((270 + 180 * HOOD_ARC) % 360) * Math.PI) / 180;

function known(armour?: string | null): ArmourClass | null {
  return armour === 'CLOTH' || armour === 'LEATHER' || armour === 'PLATE' ? armour : null;
}

export function handsProfile(hands?: string | null): HandsProfile {
  const k = known(hands);
  return k ? HANDS[k] : BARE_HANDS;
}
export function legsProfile(legs?: string | null): LegsProfile {
  const k = known(legs);
  return k ? LEGS[k] : BARE_LEGS;
}
export function feetProfile(feet?: string | null): FeetProfile {
  const k = known(feet);
  return k ? FEET[k] : BARE_FEET;
}

/** Limb thickness at the joint and at the far end — see `crusader.ts` for why tapered. */
export const ARM_TOP = 0.17;
export const ARM_BOTTOM = 0.13;
export const LEG_TOP = 0.21;
export const LEG_BOTTOM = 0.15;

/**
 * Builds the four limbs — upper and lower halves, hands, feet — dressed by the worn gear.
 *
 * Each half is a capsule hanging from its own joint, so the elbow and the knee are real
 * bends: the upper arm reaches from the shoulder to `foreR`, the forearm from there to
 * the hand. `rig.hold` is applied by the pose, not here.
 */
export function buildLimbs(scene: Scene, rig: Rig, palette: ModelPalette, gear: WornGear): void {
  const parts = rig.parts;
  const hands = handsProfile(gear.hands);
  const legs = legsProfile(gear.legs);
  const feet = feetProfile(gear.feet);

  const sleeve = palette.cell(GEAR.chainmail, 'steel');
  const cuffMat = palette.cell(hands.cell, hands.material);
  const thighMat = palette.cell(legs.cell, legs.material);
  const bootMat = palette.cell(feet.cell, feet.material);
  const skin = palette.get('rust');

  // Limb lengths come from the rig's own joints, so geometry and pivots cannot disagree.
  const upper = -rig.foreR.position.y;
  const fore = -rig.handR.position.y;
  const thigh = -rig.shinR.position.y;
  const shin = thigh;

  for (const [side, arm, forearm, hand] of [
    ['R', rig.armR, rig.foreR, rig.handR],
    ['L', rig.armL, rig.foreL, rig.handL],
  ] as const) {
    capsule(scene, arm, parts, {
      name: `cru-arm${side}`,
      height: upper + 0.06,
      radiusTop: ARM_TOP / 2,
      radiusBottom: (ARM_TOP + ARM_BOTTOM) / 4,
      at: [0, -upper / 2, 0],
      material: sleeve,
    });
    capsule(scene, forearm, parts, {
      name: `cru-fore${side}`,
      height: fore + 0.04,
      radiusTop: ((ARM_TOP + ARM_BOTTOM) / 4) * hands.cuff,
      radiusBottom: (ARM_BOTTOM / 2) * hands.cuff,
      at: [0, -fore / 2, 0],
      material: cuffMat,
    });
    if (hands.flare) {
      // The gauntlet's cuff: a ring at the elbow end of the forearm, wider than the arm.
      torus(scene, forearm, parts, {
        name: `cru-cuff${side}`,
        diameter: ARM_TOP * hands.cuff * 0.95,
        thickness: 0.05,
        at: [0, -0.04, 0],
        material: cuffMat,
        tessellation: 8,
      });
    }
    // A fist. Small, and the one part of the arm that is never armour — it is what holds
    // the weapon, so it has to read as a hand and not as more sleeve.
    sphere(scene, hand, parts, {
      name: `cru-hand${side}`,
      size: [0.12, 0.13, 0.12],
      at: [0, -0.02, 0.01],
      material: hands.flare ? cuffMat : skin,
      segments: 4,
    });
  }

  for (const [side, leg, shinNode] of [
    ['R', rig.legR, rig.shinR],
    ['L', rig.legL, rig.shinL],
  ] as const) {
    capsule(scene, leg, parts, {
      name: `cru-leg${side}`,
      height: thigh + 0.06,
      radiusTop: (LEG_TOP / 2) * legs.girth,
      radiusBottom: ((LEG_TOP + LEG_BOTTOM) / 4) * legs.girth,
      at: [0, -thigh / 2, 0],
      material: thighMat,
    });
    if (legs.cop) {
      sphere(scene, shinNode, parts, {
        name: `cru-knee${side}`,
        size: [0.2, 0.16, 0.2],
        at: [0, 0, 0.02],
        material: thighMat,
        segments: 4,
      });
    }
    capsule(scene, shinNode, parts, {
      name: `cru-shin${side}`,
      height: shin - feet.shaft + 0.04,
      radiusTop: (LEG_TOP + LEG_BOTTOM) / 4,
      radiusBottom: LEG_BOTTOM / 2,
      at: [0, -(shin - feet.shaft) / 2, 0],
      material: thighMat,
    });
    // The boot: a shaft up the shin (as tall as the class wears it) over a rounded toe box.
    // Worn leather or iron, **never** the sheet's boot cell — see the note in the atlas: that
    // cell is a picture of a boot, and mapped onto one it paints a small boot inside a black
    // square so the foot reads as a hole.
    if (feet.shaft > 0.03) {
      cylinder(scene, shinNode, parts, {
        name: `cru-bootshaft${side}`,
        height: feet.shaft + 0.08,
        diameterTop: LEG_BOTTOM * 1.2,
        diameterBottom: LEG_BOTTOM * 1.1,
        at: [0, -shin + feet.shaft / 2 + 0.02, 0],
        material: bootMat,
        tessellation: 8,
      });
    }
    capsule(scene, shinNode, parts, {
      name: `cru-boot${side}`,
      height: feet.toe,
      radiusTop: 0.075,
      radiusBottom: 0.075,
      at: [0, -shin + 0.03, feet.toe / 2 - 0.08],
      rotation: [Math.PI / 2, 0, 0],
      material: bootMat,
      tessellation: 8,
    });
  }
}

/**
 * Builds the head: a skull, the face plate, the neck, and the headgear the class wears.
 *
 * `plated` is what the face follows — the iron visor grate goes with a helm, the amber eyes
 * with anything open — and it is the head *style*, not the chest, that decides it now.
 */
export function buildHead(
  scene: Scene,
  rig: Rig,
  palette: ModelPalette,
  style: HeadStyle,
  faceTilt: number,
): void {
  const parts = rig.parts;
  const bronze = palette.get('bronze');
  const cloth = palette.get('cloth');
  const helmMat = palette.cell(GEAR.ironPlate, 'steel');
  const capMat = palette.cell(GEAR.leatherWorn, 'rust');

  // **The face is on the skull, not on a plate in front of it.** The box rig painted the face
  // cell on a small tilted box, which on a round head read as a square sticker — reported as
  // *the face texture is too squared, it needs to be attached to the actual head*. The cell now
  // goes on the skull sphere itself, fitted to its front patch (`FACE_FIT`) so the picture
  // covers roughly 100° of the head and the clamped edge colour — dark skin, or dark iron —
  // wraps the rest, which the hood, the cap or the helm covers anyway. `selfLit` for the amber
  // eyes as before: the painting is the light.
  const faceCell = style === 'helm' ? FACE.visorIron : FACE.eyesAmber;
  const face = palette.cell(faceCell, style === 'helm' ? 'steel' : 'ember', style === 'helm' ? 'lit' : 'selfLit', FACE_FIT);
  sphere(scene, rig.head, parts, {
    name: 'cru-skull',
    size: [0.28, 0.3, 0.27],
    at: [0, 0.1, 0],
    // Tilted up to meet the camera, exactly as the plate was (`FACE_TILT`): the view arrives
    // 45° above the horizon, and half of that keeps the face readable without pointing the
    // chin at the horizon.
    rotation: [-faceTilt, 0, 0],
    material: face,
    segments: 8,
  });

  switch (style) {
    case 'helm':
      // A turned dome with a brow band and a crest: beaten metal, which is what separates it
      // from a hood at a glance and is how a player tells the classes apart. It starts above
      // the eyes, so the visor grate painted on the skull shows under its rim.
      lathe(scene, rig.head, parts, {
        name: 'cru-helm',
        profile: [
          [0.185, 0],
          [0.19, 0.05],
          [0.16, 0.11],
          [0.1, 0.17],
          [0, 0.2],
        ],
        at: [0, 0.1, -0.01],
        material: helmMat,
        tessellation: 10,
      });
      // The crest: one low strip over the crown, which is what stops a dome reading as a
      // bucket. Sunk into the dome so only a ridge shows — a taller one read as a candle.
      box(scene, rig.head, parts, {
        name: 'cru-helm-crest',
        size: [0.035, 0.06, 0.28],
        at: [0, 0.3, -0.02],
        material: bronze,
      });
      break;
    case 'cap':
      // A leather skullcap: a low dome over the crown with a rolled brim. Lower and rounder
      // than the helm, softer than the hood's point — the third silhouette the set needed.
      // Seated high: the brim clears the eyes rather than sitting across them.
      sphere(scene, rig.head, parts, {
        name: 'cru-cap',
        size: [0.34, 0.28, 0.34],
        at: [0, 0.16, -0.01],
        material: capMat,
        segments: 6,
        slice: 0.55,
      });
      torus(scene, rig.head, parts, {
        name: 'cru-cap-brim',
        diameter: 0.32,
        thickness: 0.055,
        at: [0, 0.165, -0.01],
        material: capMat,
        tessellation: 10,
      });
      break;
    default:
      // The pointed cloth hood — **open at the front**, since the face is on the skull inside
      // it now rather than on a plate in front of it. `HOOD_ARC` of the circumference, turned
      // so the gap is centred forward: Babylon starts an arc at +X and sweeps through −Z, so
      // the gap's centre sits at 270° + 180°·arc, and `HOOD_YAW` brings that to the front.
      cylinder(scene, rig.head, parts, {
        name: 'cru-hood',
        height: 0.38,
        diameterTop: 0.05,
        diameterBottom: 0.4,
        at: [0, 0.12, -0.02],
        rotation: [0, HOOD_YAW, 0],
        material: cloth,
        tessellation: 10,
        arc: HOOD_ARC,
      });
  }

  // The neck and its gorget: a short collar rather than a bar.
  cylinder(scene, rig.head, parts, {
    name: 'cru-gorget',
    height: 0.1,
    diameterTop: 0.24,
    diameterBottom: 0.3,
    at: [0, -0.07, 0],
    material: bronze,
    tessellation: 8,
  });
}
